/**
 * «منشی هوشمند پارسینا» — the shell's side of the secretary (PM-0080).
 *
 * `SecretaryProvider` wraps the shell once, so the record button — a sidebar row
 * on a desk (SecretaryRecorder), a round button on a phone (SecretaryFab) — and
 * what it brings back belong to no screen and survive moving between them.
 *
 * `SecretaryHost` is mounted once beside them and owns:
 *   • the keys — F1 records and stops from any screen, Esc throws a recording
 *     away, Shift+F1 opens the newest result;
 *   • the notifications (SecretaryNotices), and where opening one goes
 *     (model.routeOf): «ثبت نوبت» filled in when nothing is missing, the record
 *     for «باز کردن پرونده», and the result's own window (SecretaryWindow) when
 *     something has to be answered or chosen first;
 *   • the hand-off to «ثبت نوبت» — the existing booking form, opened prefilled —
 *     and what happens after a write: the toast, and telling پذیرش to re-read.
 *
 * Who may use it: anyone the BFF would answer. The BFF checks each tool against
 * the caller's own permissions, so there is no permission key for the button —
 * a member who may only read gets answers and refusals, not a missing button.
 */
import React from 'react';
import { Alert, useToast } from '@parsina/framework';

import { useAppNavigate } from '../hooks/useAppNavigate';
import { useTranslation } from '../i18n';
import { reportAppointmentsChanged } from '../lib/appointmentsSignal';
import { useShortcut } from '../lib/shortcuts.jsx';
import BookAppointmentModal from '../screens/Reception/BookAppointmentModal';
import { bookingHandoff, routeOf } from './model';
import SecretaryNotices from './SecretaryNotices';
import SecretaryWindow from './SecretaryWindow';
import { useSecretaryController } from './useSecretaryController';

const SecretaryContext = React.createContext(null);

/** The record button's state and actions — null outside a SecretaryProvider. */
export function useSecretaryRecorder() {
  return React.useContext(SecretaryContext);
}

export function SecretaryProvider({ children }) {
  const controller = useSecretaryController();
  return <SecretaryContext.Provider value={controller}>{children}</SecretaryContext.Provider>;
}

export default function SecretaryHost({ sidebarWidth = 0 }) {
  const c = useSecretaryRecorder();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useAppNavigate();
  const [booking, setBooking] = React.useState(null);
  const busy = Boolean(booking || c?.openItem);

  /** Where a result goes when it is opened. */
  const openResult = React.useCallback((item) => {
    const route = item.error ? 'window' : routeOf(item.result, new Set(item.skipped || []));
    if (route === 'form' || route === 'formDay') {
      c.dismiss(item.id);
      setBooking(bookingHandoff(item.result.booking, route === 'form' ? 0 : -1));
      return;
    }
    if (route === 'navigate') {
      c.dismiss(item.id);
      navigate('patient', { id: item.result.patient.id });
      return;
    }
    c.open(item.id);
  }, [c, navigate]);

  useShortcut('f1', () => c?.toggle(), {
    label: t('secretary.shortcuts.record'), group: t('shortcuts.groups.general'), enabled: Boolean(c) && !busy,
  });
  useShortcut('esc', () => c?.discard(), {
    label: t('secretary.shortcuts.discard'), group: t('shortcuts.groups.general'), enabled: Boolean(c?.recording) && !busy,
  });
  useShortcut('shift+f1', () => { const items = c?.items || []; if (items.length) openResult(items[items.length - 1]); }, {
    label: t('secretary.shortcuts.openNewest'), group: t('shortcuts.groups.general'), enabled: Boolean(c?.items.length) && !busy,
  });

  if (!c) return null;
  const item = c.openItem;
  return (
    <>
      <SecretaryNotices items={c.items} onOpen={openResult} onDismiss={c.dismiss} sidebarWidth={sidebarWidth} />
      {item ? (
        <SecretaryWindow
          key={item.id}
          item={item}
          onClose={c.close}
          onUpdate={(patch) => c.update(item.id, patch)}
          onBook={(handoff) => { c.dismiss(item.id); setBooking(handoff); }}
          onDone={(message) => { c.dismiss(item.id); if (message) toast.success(message); }}
          onOpenPatient={(id) => { c.dismiss(item.id); navigate('patient', { id }); }}
          onDismiss={() => c.dismiss(item.id)}
          onRecordAgain={() => { c.dismiss(item.id); c.start(); }}
        />
      ) : null}
      {booking ? (
        <BookAppointmentModal
          presetDoctorId={booking.doctorId}
          presetPatient={booking.patient}
          presetNewPatient={booking.newPatient}
          initialDate={booking.initialDate}
          initialSlotLabel={booking.initialSlotLabel}
          notice={(
            <Alert variant="info" data-testid="secretary-form-notice">
              {t('secretary.booking.formNotice')}
              {booking.newPatient ? ` ${t('secretary.booking.formNoticeNewPatient')}` : ''}
              {booking.doctorAssumed ? ` ${t('secretary.booking.formNoticeAssumed')}` : ''}
            </Alert>
          )}
          onClose={() => setBooking(null)}
          onBooked={(message) => {
            if (message) toast.success(message);
            reportAppointmentsChanged();
          }}
        />
      ) : null}
    </>
  );
}
